// API utilities for backend communication

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || `${window.location.protocol}//${window.location.hostname}:3001/api`;

// Types
export interface ApiAppointment {
  id: string;
  full_name: string;
  phone: string;
  email?: string;
  department: string;
  preferred_date: string;
  message?: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  created_at: string;
  updated_at: string;
}

export interface ApiNewsItem {
  id: string;
  title: string;
  content: string;
  author: string;
  category: 'news' | 'announcement' | 'event';
  status: 'published' | 'draft';
  image_url?: string;
  date: string;
  created_at: string;
  updated_at: string;
}

export interface CreateAppointmentData {
  fullName: string;
  phone: string;
  email?: string;
  department: string;
  preferredDate: string;
  message?: string;
}

export interface CreateNewsData {
  title: string;
  content: string;
  author: string;
  category: 'news' | 'announcement' | 'event';
  status: 'published' | 'draft';
  imageUrl?: string;
  date: string;
}

export interface ApiGalleryItem {
  id: string;
  title: string;
  description?: string;
  image_url: string;
  category: string;
  created_at: string;
  updated_at: string;
}

export interface CreateGalleryData {
  title: string;
  description?: string;
  imageUrl: string;
  category: string;
}

// Auth utilities
let authToken: string | null = null;

// Initialize auth token
const initializeAuth = () => {
  // Try to get from localStorage first
  authToken = localStorage.getItem('hospital_auth_token');
  
  // If no token, create a default one for development
  if (!authToken) {
    console.log('🔐 No auth token found, creating default token for development');
    authToken = 'dev-admin-token-' + Date.now();
    localStorage.setItem('hospital_auth_token', authToken);
  }
  
  console.log('🔐 Auth token initialized:', authToken ? 'Present' : 'Missing');
};

// Initialize on module load
initializeAuth();

export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (token) {
    localStorage.setItem('hospital_auth_token', token);
  } else {
    localStorage.removeItem('hospital_auth_token');
  }
};

const getAuthHeaders = () => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  
  return headers;
};

// API functions
export const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config: RequestInit = {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// Auth API
export const loginUser = async (email: string, password: string) => {
  const response = await apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  if (response.token) {
    setAuthToken(response.token);
  }

  return response;
};

export const logoutUser = () => {
  setAuthToken(null);
};

// Appointments API
export const fetchAppointments = async (): Promise<ApiAppointment[]> => {
  console.log('📡 Fetching appointments from API...');
  return await apiRequest('/appointments');
};

export const createAppointment = async (data: CreateAppointmentData): Promise<ApiAppointment> => {
  console.log('🌐 Sending appointment to API:', data);
  
  return await apiRequest('/appointments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateAppointmentStatus = async (id: string, status: string): Promise<void> => {
  await apiRequest(`/appointments/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
};

export const deleteAppointment = async (id: string): Promise<void> => {
  await apiRequest(`/appointments/${id}`, {
    method: 'DELETE',
  });
};

// News API
export const fetchNews = async (status?: string): Promise<ApiNewsItem[]> => {
  const endpoint = status ? `/news?status=${status}` : '/news';
  return await apiRequest(endpoint);
};

export const createNews = async (data: CreateNewsData): Promise<ApiNewsItem> => {
  return await apiRequest('/news', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateNews = async (id: string, data: Partial<CreateNewsData>): Promise<void> => {
  await apiRequest(`/news/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteNews = async (id: string): Promise<void> => {
  await apiRequest(`/news/${id}`, {
    method: 'DELETE',
  });
};

// Gallery API
export const fetchGallery = async (): Promise<ApiGalleryItem[]> => {
  return await apiRequest('/gallery');
};

export const createGallery = async (data: CreateGalleryData): Promise<ApiGalleryItem> => {
  return await apiRequest('/gallery', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateGallery = async (id: string, data: Partial<CreateGalleryData>): Promise<void> => {
  await apiRequest(`/gallery/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteGallery = async (id: string): Promise<void> => {
  await apiRequest(`/gallery/${id}`, {
    method: 'DELETE',
  });
};

// Image upload API
export const uploadImage = async (file: File): Promise<{ imageUrl: string }> => {
  console.log('📤 Starting image upload:', file.name, file.size, file.type);
  
  // Ensure we have an auth token
  if (!authToken) {
    console.log('🔐 No auth token, initializing...');
    initializeAuth();
  }
  
  const formData = new FormData();
  formData.append('image', file);
  
  try {
    console.log('📤 Sending upload request to:', `${API_BASE_URL}/upload`);
    console.log('📤 File details:', {
      name: file.name,
      size: file.size,
      type: file.type
    });
    
    const response = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
      body: formData,
    });
    
    console.log('📤 Upload response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Upload failed with status:', response.status);
      console.error('❌ Error response:', errorText);
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log('✅ Upload successful:', result);
    
    // Log the returned image URL
    console.log('🔗 Returned image URL:', result.imageUrl);
    
    // Test if the image is accessible
    const testUrl = result.imageUrl.startsWith('http') 
      ? result.imageUrl 
      : `${window.location.origin}${result.imageUrl}`;
    console.log('🔗 Testing image URL:', testUrl);
    
    // Try to fetch the image to verify it's accessible
    try {
      const testResponse = await fetch(testUrl);
      console.log('🔗 Image accessibility test:', testResponse.status);
    } catch (testError) {
      console.warn('⚠️ Image accessibility test failed:', testError);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Upload error:', error);
    throw new Error(`Upload failed: ${(error as Error).message || 'Unknown error'}`);
  }
};

// Dashboard API
export const fetchDashboardStats = async () => {
  return await apiRequest('/dashboard/stats');
};